import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NuplayWordmark } from "../components/NuplayWordmark";
import { TSC_LOGO_SRC } from "./tscConsoleSkin";
import { DEFAULT_TSC_DEMO, TscDemo, tscVoices } from "./tscDemos";
import { TscResetDemo } from "./TscResetDemo";
import "./tsc.css";

// Shared Tractor Supply chrome for the TSC landing and customer picker: a utility bar,
// the white logo header and the dark footer, laid out like tractorsupply.com.
// `toggle`: the Riley / Mike switch, shown at the right of the header (customer list).

export function TscHeader({ demo = DEFAULT_TSC_DEMO, toggle }: { demo?: TscDemo; toggle?: ReactNode }) {
  const navigate = useNavigate();
  const name = demo.persona.name;
  return (
    <>
      <div className="tsc-utility">
        <div className="tsc-wrap tsc-utility-row">
          <span>Customer support demo · AI voice associate</span>
          <span className="tsc-utility-powered">Powered by <NuplayWordmark height={11} mono /></span>
        </div>
      </div>
      <header className="tsc-header">
        <div className="tsc-wrap tsc-header-row">
          <button type="button" className="tsc-logo-btn" onClick={() => navigate(demo.home)} aria-label="Tractor Supply demo home">
            <img src={TSC_LOGO_SRC} alt="Tractor Supply Co." className="tsc-logo-img" />
          </button>
          <div className="tsc-header-title">
            <span className="tsc-header-name">{name}</span>
            <span className="tsc-header-sub">Customer Support</span>
          </div>
          {toggle && tscVoices(demo).length > 1 && <div className="tsc-header-toggle">{toggle}</div>}
        </div>
      </header>
      <nav className="tsc-catrow" aria-label="Demo sections">
        <div className="tsc-wrap tsc-catrow-row">
          <a href={`${demo.home}#crew`}>{`How ${name} works`}</a>
          <TscResetDemo variant="nav" />
          <a href={`${demo.home}#before`}>Before you test</a>
        </div>
      </nav>
    </>
  );
}

export function TscFooter() {
  return (
    <footer className="tsc-footer">
      <div className="tsc-wrap tsc-footer-row">
        <div className="tsc-footer-h">Tractor Supply customer support demo</div>
        <div className="tsc-footer-powered">Powered by <NuplayWordmark height={15} mono /></div>
      </div>
    </footer>
  );
}

// `agent`: the agent's own face; without a photo it shows the initial in white on TSC red.
export function TscAvatar({ src, name, size = 48, agent = false }: { src?: string; name: string; size?: number; agent?: boolean }) {
  const [err, setErr] = useState(false);
  const initials = name.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase();
  const photo = !!src && !err;
  return (
    <span className={`tsc-avatar${agent && !photo ? " tsc-avatar-agent" : ""}`} style={{ width: size, height: size, fontSize: Math.round(size * (agent ? 0.46 : 0.34)) }} aria-hidden={agent || undefined}>
      {photo ? <img src={src} alt="" onError={() => setErr(true)} /> : initials}
    </span>
  );
}

// The Riley / Mike switch: the same crew in two voices. Renders nothing for a demo without
// one (the multilingual page). Disabled while a call is connecting or live.
export function TscAgentToggle({ demo, onPick, disabled = false, disabledNote }: {
  demo: TscDemo;
  onPick: (d: TscDemo) => void;
  disabled?: boolean;
  disabledNote?: string;
}) {
  const voices = tscVoices(demo);
  if (voices.length < 2) return null;
  return (
    <span className="tsc-voice" role="radiogroup" aria-label="Agent">
      {voices.map((d) => {
        const on = d.agentSlug === demo.agentSlug;
        return (
          <button
            key={d.agentSlug}
            type="button"
            role="radio"
            aria-checked={on}
            className={`tsc-voice-btn${on ? " on" : ""}`}
            disabled={disabled}
            title={disabled ? disabledNote : on ? undefined : `Talk to ${d.persona.name}`}
            onClick={() => { if (!on) onPick(d); }}
          >
            <TscAvatar src={d.persona.avatar} name={d.persona.name} size={26} agent />
            <span>{d.persona.name}</span>
          </button>
        );
      })}
    </span>
  );
}
